import { cn } from '@src/lib/utils';

import i18nConfig from '@app/configs/i18n';
import { queryClient } from '@app/configs/query';

import { PortalContainerProvider } from '@shared/providers/PortalContainerContext';
import { useThemeStore } from '@shared/stores/theme';

import {
    GetAccessToken,
    registerWebComponentAuth,
    WebComponentAuth,
} from './axiosWebComponents';
import {
    acquireWebComponentRuntime,
    setWebComponentTheme,
    type WebComponentRuntime,
} from './runtime';

import { CacheProvider } from '@emotion/react';
import ShadcnErrorBoundary from '@shadcn-features/suspense/ShadcnErrorBoundary';
import { QueryClientProvider } from '@tanstack/react-query';
import {
    ComponentType,
    createElement,
    Suspense,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { I18nextProvider } from 'react-i18next';
import * as ReactRouterDom from 'react-router-dom';

type WebComponentHostProps = {
    active?: boolean;
    accessToken?: string;
    getAccessToken?: GetAccessToken;
    lng?: string;
    theme?: string | null;
};

let pendingLanguageChange = Promise.resolve();

const useWebComponentLanguage = (lng: string | undefined, active: boolean) => {
    useEffect(() => {
        if (!lng || !active) return;
        let canceled = false;
        pendingLanguageChange = pendingLanguageChange
            .catch(() => {})
            .then(async () => {
                if (!canceled && i18nConfig.language !== lng) {
                    await i18nConfig.changeLanguage(lng);
                }
            });
        void pendingLanguageChange.catch((error: unknown) => {
            console.error('[web-components] Language change failed', error);
        });
        return () => {
            canceled = true;
        };
    }, [lng, active]);
};

const useWebComponentAuth = ({
    accessToken,
    getAccessToken,
}: Pick<WebComponentHostProps, 'accessToken' | 'getAccessToken'>) => {
    if (Boolean(accessToken) !== Boolean(getAccessToken)) {
        throw new Error(
            'accessToken and getAccessToken must be provided together.',
        );
    }

    const auth = useMemo<WebComponentAuth | null>(
        () =>
            accessToken && getAccessToken
                ? { accessToken, getAccessToken }
                : null,
        [accessToken, getAccessToken],
    );
    const [registeredAuth, setRegisteredAuth] = useState<
        WebComponentAuth | null | undefined
    >(undefined);

    useEffect(() => {
        const unregister = registerWebComponentAuth(auth, () =>
            queryClient.clear(),
        );
        setRegisteredAuth(auth);
        return unregister;
    }, [auth]);

    return registeredAuth === auth;
};

const useWebComponentRuntime = (themeProp?: string | null) => {
    const cleanupRef = useRef<(() => void) | null>(null);
    const [runtime, setRuntime] = useState<WebComponentRuntime | null>(null);

    useEffect(() => {
        setWebComponentTheme(themeProp);
    }, [themeProp]);

    const setupStyleContainer = useCallback(
        (styleContainer: HTMLDivElement | null) => {
            if (!styleContainer) {
                cleanupRef.current?.();
                cleanupRef.current = null;
                setRuntime(null);
                return;
            }

            if (cleanupRef.current) return;

            const rootNode = styleContainer.getRootNode();
            if (!(rootNode instanceof ShadowRoot)) return;

            const nextRuntime = acquireWebComponentRuntime(
                rootNode,
                styleContainer,
            );
            cleanupRef.current = nextRuntime.release;
            setRuntime(nextRuntime);
        },
        [],
    );

    return { runtime, setupStyleContainer };
};

export function WrapperWebComponents<P extends object>(
    Component: ComponentType<P>,
) {
    const NewComponent = (props: P & WebComponentHostProps) => {
        const theme = useThemeStore((x) => x.theme);
        const { runtime, setupStyleContainer } = useWebComponentRuntime(
            props.theme,
        );

        const active = props.active !== false;
        useLayoutEffect(() => {
            runtime?.setActive(active);
        }, [active, runtime]);

        useWebComponentLanguage(props.lng, active);
        const authReady = useWebComponentAuth(props);

        return (
            <div
                data-scope-office
                className={cn(theme === 'dark' ? 'dark' : undefined)}
                data-theme={theme}
            >
                <div
                    ref={setupStyleContainer}
                    data-office-wc-emotion-styles=''
                />
                {runtime && authReady ? (
                    <CacheProvider value={runtime.emotionCache}>
                        <PortalContainerProvider
                            value={{
                                active,
                                container: runtime.portalContainer,
                                emotionCache: runtime.portalEmotionCache,
                            }}
                        >
                            <ShadcnErrorBoundary>
                                <QueryClientProvider client={queryClient}>
                                    <ReactRouterDom.BrowserRouter>
                                        <I18nextProvider i18n={i18nConfig}>
                                            <Suspense fallback={null}>
                                                {createElement(
                                                    Component,
                                                    props as P,
                                                )}
                                            </Suspense>
                                        </I18nextProvider>
                                    </ReactRouterDom.BrowserRouter>
                                </QueryClientProvider>
                            </ShadcnErrorBoundary>
                        </PortalContainerProvider>
                    </CacheProvider>
                ) : null}
            </div>
        );
    };

    return NewComponent;
}
